import {
  disclaimerCopyForKey,
  inferDisclaimerKeyFromAttribution,
  pickerCaveatForBroker,
} from "../constants/marketDataDisclaimers";
import type { MarketDataAttribution } from "../types/marketDataAttribution";
import type {
  InstrumentSetupResolvedRow,
  InstrumentSetupRow,
} from "../types/instrumentSetup";
import type { MarketDataSourceOption } from "../types/marketDataSources";
import { brokerRequiresMdFetchConsent } from "../components/MarketData/MarketDataFetchConsentCheckbox";
import { isBrokerConnectMarketDataOption } from "./marketDataConnectGate";
import { hasSavedMarketDataSourcePreference, marketDataBrokerKey, humanizeMarketDataBrokerSlug, stripInternalMarketDataLabelSuffix, isGenericTwelveDataListingVenue } from "./marketDataSourceChoice";

export type MarketDataDisclaimerBlocks = {
  collapsed: string;
  expanded: string;
  shortLabel: string;
};

/** User-facing short label for chart footnotes (no internal listing keys). */
export function formatMarketDataSourceShortLabel(input: {
  attribution?: MarketDataAttribution | null;
  sourceLabel?: string | null;
  marketDataBrokerDisplay?: string | null;
}): string {
  const fromAttribution = formatShortLabelFromAttribution(input.attribution);
  if (fromAttribution) return fromAttribution;

  const stripped = stripInternalMarketDataLabelSuffix(input.sourceLabel?.trim() || "");
  if (stripped) return stripped;

  const brokerDisplay = input.marketDataBrokerDisplay?.trim();
  if (brokerDisplay) return `OHLC via ${brokerDisplay}`;

  return "Chart OHLC source";
}

function attributionNamesMatch(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** e.g. "from BINANCE via CoinAPI" or "via Hyperliquid" when venue === provider. */
function formatExchangeDirectSourcePhrase(provider: string, venue?: string): string {
  const p = provider.trim();
  const v = venue?.trim();
  if (v && !attributionNamesMatch(p, v)) {
    return `from ${v} via ${p}`;
  }
  return `via ${p}`;
}

function formatShortLabelFromAttribution(
  attribution: MarketDataAttribution | null | undefined
): string | undefined {
  if (!attribution) return undefined;
  const provider = attribution.provider_display?.trim() || attribution.provider?.trim();
  const venue = attribution.venue?.trim();
  if (provider && venue && !attributionNamesMatch(provider, venue)) {
    return `OHLC via ${provider} · ${venue}`;
  }
  if (provider) {
    if (
      attribution.provenance_tier === "vendor_composite" &&
      (attribution.provider?.trim().toLowerCase() === "twelvedata" ||
        provider.toLowerCase() === "twelve data")
    ) {
      return `OHLC via ${provider} (aggregated)`;
    }
    return `OHLC via ${provider}`;
  }
  return undefined;
}

/** Prefer OHLC lane on `market_data_sources[]` over stale top-level attribution (venue often null on scan). */
export function selectedMarketDataSourceForSetupRow(
  row: Pick<
    InstrumentSetupRow | InstrumentSetupResolvedRow,
    "selected_market_data_source" | "market_data_sources"
  >
): MarketDataSourceOption | undefined {
  return (
    row.selected_market_data_source ??
    row.market_data_sources?.find((source) => source.is_selected) ??
    row.market_data_sources?.find((source) => source.is_default) ??
    row.market_data_sources?.[0]
  );
}

export function effectiveMarketDataAttributionForSetupRow(
  row: Pick<
    InstrumentSetupRow | InstrumentSetupResolvedRow,
    "market_data_attribution" | "selected_market_data_source" | "market_data_sources"
  >
): MarketDataAttribution | undefined {
  return (
    inferAttributionFromSource(selectedMarketDataSourceForSetupRow(row)) ??
    row.market_data_attribution
  );
}

export function resolveMarketDataDisclaimer(
  attribution: MarketDataAttribution | null | undefined,
  sourceLabel?: string | null
): MarketDataDisclaimerBlocks {
  const disclaimerKey = inferDisclaimerKeyFromAttribution(attribution ?? undefined);
  const copy = disclaimerCopyForKey(disclaimerKey);
  return {
    collapsed: copy.collapsed,
    expanded: copy.expanded,
    shortLabel: formatMarketDataSourceShortLabel({ attribution, sourceLabel }),
  };
}

export function resolveLayer2ConsentLabel(input: {
  symbol: string;
  attribution?: MarketDataAttribution | null;
  source?: MarketDataSourceOption | null;
}): string {
  const symbol = input.symbol.trim();
  const tier = input.attribution?.provenance_tier;
  const provider =
    input.attribution?.provider_display?.trim() ||
    input.source?.broker_display?.trim() ||
    input.source?.market_data_broker_display?.trim() ||
    input.source?.broker?.trim() ||
    "market data provider";
  const venue =
    input.attribution?.venue?.trim() ||
    input.source?.venue_label?.trim() ||
    input.source?.venue?.trim();

  if (tier === "broker_feed") {
    return `I consent to Tessera fetching OHLC from my connected ${provider} account for ${symbol} on this trade account.`;
  }
  if (tier === "vendor_composite") {
    return `I understand chart data for ${symbol} comes from a third-party consolidated feed (${provider}) and may differ from my execution broker.`;
  }
  if (tier === "exchange_direct") {
    return `I understand chart data for ${symbol} is sourced ${formatExchangeDirectSourcePhrase(provider, venue)} and may differ from my execution broker.`;
  }
  return `I consent to using the selected market data listing for OHLC analysis of ${symbol} on this trade account.`;
}

/**
 * Per-instrument (Layer 2) consent for instrument-setup Confirm.
 * Broker feeds (Liquid Charts, cTrader, …) require an explicit checkbox even when BE omits
 * `requires_market_data_consent` on a first-time save.
 */
export function requiresMarketDataConsentForSetup(input: {
  row: Pick<
    InstrumentSetupRow,
    | "requires_market_data_consent"
    | "saved_market_data_broker"
    | "market_data_broker"
    | "saved_ibkr_conid"
    | "saved_ibkr_exchange"
  >;
  source?: MarketDataSourceOption | null;
}): boolean {
  if (input.source?.unavailable_reason === "market_data_consent_required") {
    // Unavailable lane → account OAuth repair, not inline Layer 2 checkbox.
    if (input.source.available === false) return false;
    return true;
  }
  if (input.row.requires_market_data_consent) {
    const activeBroker = marketDataBrokerKey(input.source ?? {});
    // Stale BYOK consent flags do not apply when the active OHLC lane is central (HL, CoinAPI, …).
    if (activeBroker && !isBrokerConnectMarketDataOption(activeBroker)) {
      return false;
    }
    return true;
  }

  const broker = marketDataBrokerKey(input.source ?? {});
  const isBrokerFeed =
    brokerRequiresMdFetchConsent(broker) ||
    inferAttributionFromSource(input.source)?.provenance_tier === "broker_feed";

  if (!isBrokerFeed) return false;

  const savedBroker = (
    input.row.saved_market_data_broker ?? input.row.market_data_broker ?? ""
  )
    .trim()
    .toLowerCase();
  if (
    !input.row.requires_market_data_consent &&
    savedBroker &&
    savedBroker === broker &&
    hasSavedMarketDataSourcePreference(input.row)
  ) {
    return false;
  }

  // Layer 2: broker-feed OHLC needs explicit consent on first save or broker switch.
  return true;
}

export function resolveSourcePickerCaveat(
  source: MarketDataSourceOption | null | undefined
): string | undefined {
  if (!source) return undefined;
  const broker = marketDataBrokerKey(source);

  if (broker === "twelvedata") {
    const venue = (source.venue_label ?? source.venue)?.trim();
    const provider =
      source.broker_display?.trim() ||
      source.market_data_broker_display?.trim() ||
      "Twelve Data";
    if (venue && !isGenericTwelveDataListingVenue(venue, provider)) {
      return undefined;
    }
    return pickerCaveatForBroker("twelvedata");
  }

  if (broker === "coinapi") {
    const venue = (source.venue_label ?? source.venue)?.trim();
    if (venue) {
      return `${venue} prices via CoinAPI — specific to that exchange, not crypto in general.`;
    }
    return pickerCaveatForBroker("coinapi");
  }

  if (broker === "databento") {
    return pickerCaveatForBroker("databento");
  }

  if (broker === "hyperliquid") {
    return pickerCaveatForBroker("hyperliquid");
  }

  if (broker === "liquidbrokers" || broker === "liquid") {
    return pickerCaveatForBroker(broker);
  }

  if (broker === "ctrader") {
    return pickerCaveatForBroker("ctrader");
  }

  return pickerCaveatForBroker(broker);
}

export function inferAttributionFromSource(
  source: MarketDataSourceOption | null | undefined
): MarketDataAttribution | undefined {
  if (!source) return undefined;
  const provider = marketDataBrokerKey(source);
  if (!provider) return undefined;

  let provenance_tier: MarketDataAttribution["provenance_tier"];
  if (provider === "twelvedata") {
    const venue = (source.venue_label ?? source.venue)?.trim();
    const providerDisplay =
      source.broker_display?.trim() ||
      source.market_data_broker_display?.trim() ||
      humanizeMarketDataBrokerSlug(provider) ||
      "Twelve Data";
    provenance_tier = isGenericTwelveDataListingVenue(venue, providerDisplay)
      ? "vendor_composite"
      : "exchange_direct";
  } else if (
    provider === "liquidbrokers" ||
    provider === "liquid" ||
    provider === "ctrader" ||
    provider === "oanda" ||
    provider === "binance"
  ) {
    provenance_tier = "broker_feed";
  } else {
    provenance_tier = "exchange_direct";
  }

  return {
    provider,
    provider_display:
      source.broker_display?.trim() ||
      source.market_data_broker_display?.trim() ||
      humanizeMarketDataBrokerSlug(provider),
    venue: (source.venue_label ?? source.venue)?.trim() || undefined,
    provenance_tier,
    disclaimer_key:
      provider === "twelvedata"
        ? provenance_tier === "exchange_direct"
          ? "market_data.disclaimer.twelvedata.exchange"
          : "market_data.disclaimer.twelvedata"
        : inferDisclaimerKeyFromAttribution({
            provider,
            md_source: provider,
            provenance_tier,
          }),
  };
}

/** Extract CoinAPI symbol_id from option_id or md_listing_key (coinapi:BINANCE_SPOT_BTC_USDT). */
export function coinapiSymbolIdFromListing(
  optionId?: string | null,
  mdListingKey?: string | null
): string | undefined {
  for (const raw of [optionId, mdListingKey]) {
    const value = raw?.trim();
    if (!value) continue;
    if (value.toLowerCase().startsWith("coinapi:")) {
      const tail = value.slice("coinapi:".length).trim();
      if (tail) return tail;
    }
    if (!value.includes(":") && value.includes("_")) {
      return value;
    }
  }
  return undefined;
}
